import { Box, Stack, Typography } from "@mui/material";
import { Check, X } from "lucide-react";

import type { PipelineStageStatus, SourcePipelineStatus } from "@/features/sources/types";
import { formatElapsed } from "@/lib/format";

const checkpointLabels = ["Download", "Transcribe", "Select", "Render"] as const;
const checkpointKeys = ["download", "transcription", "selection", "render"] as const;

type CheckpointKey = (typeof checkpointKeys)[number];

export function PipelineProgress({ pipeline, showDetail = false }: { pipeline: SourcePipelineStatus; showDetail?: boolean }) {
  const stages = [
    pipeline.download,
    pipeline.transcription,
    pipeline.moment_selection,
    pipeline.rendering
  ];
  const progress = stages.reduce((total, stage) => total + stageFraction(stage), 0) / stages.length * 100;
  const activeIndex = stages.findIndex((stage) => stage.status === "running" || stage.status === "queued" || stage.status === "failed");
  const detail = showDetail && activeIndex >= 0 ? stageDetail(checkpointKeys[activeIndex], stages[activeIndex]) : "";

  return (
    <Stack spacing={1}>
      <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Typography variant="caption" color="text.secondary" noWrap>{pipeline.current_step}</Typography>
        <Typography variant="caption" color="text.secondary" fontWeight={750}>{Math.round(progress)}%</Typography>
      </Stack>

      <Box sx={{ position: "relative", pt: 0.25 }}>
        <Box sx={{ position: "absolute", left: "12.5%", right: "12.5%", top: 8, height: 3, borderRadius: 3, bgcolor: "divider", overflow: "hidden" }}>
          <Box sx={{ width: `${Math.min(100, progress * 4 / 3)}%`, height: "100%", bgcolor: pipeline.has_error ? "error.main" : "primary.main", transition: "width 300ms ease" }} />
        </Box>

        <Box sx={{ position: "relative", display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}>
          {stages.map((stage, index) => {
            const completed = stage.status === "completed" || stage.status === "skipped";
            const failed = stage.status === "failed";
            const active = stage.status === "running" || stage.status === "queued";
            return (
              <Stack key={checkpointLabels[index]} spacing={0.65} alignItems="center">
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    color: completed || failed ? "common.white" : "text.secondary",
                    bgcolor: failed ? "error.main" : completed ? "primary.main" : "background.paper",
                    border: 2,
                    borderColor: failed ? "error.main" : active ? "primary.main" : "divider",
                    boxShadow: active ? "0 0 0 4px rgba(139,124,246,.14)" : "none",
                    transition: "all 180ms ease"
                  }}
                >
                  {completed ? <Check size={10} strokeWidth={3} /> : failed ? <X size={10} strokeWidth={3} /> : null}
                </Box>
                <Typography variant="caption" color={active || completed ? "text.primary" : "text.secondary"} fontWeight={active ? 800 : 650} sx={{ fontSize: "0.66rem" }}>
                  {checkpointLabels[index]}
                </Typography>
                {stage.duration_seconds !== null ? <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.58rem" }}>{formatElapsed(stage.duration_seconds)}</Typography> : null}
              </Stack>
            );
          })}
        </Box>
      </Box>

      {detail ? (
        <Typography variant="caption" color="text.secondary" sx={{ pt: 0.5 }}>{detail}</Typography>
      ) : null}
    </Stack>
  );
}

function stageFraction(stage: PipelineStageStatus) {
  if (stage.status === "completed" || stage.status === "skipped") return 1;
  if (stage.status === "failed" || stage.status === "waiting") return 0;
  if (stage.completed_count !== null && stage.total_count) {
    return Math.max(0.1, Math.min(0.95, stage.completed_count / stage.total_count));
  }
  return stage.status === "running" ? 0.5 : 0.15;
}

function text(diagnostics: Record<string, unknown> | null, key: string): string {
  const value = diagnostics?.[key];
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  return "";
}

/** The one line that answers "what is it doing right now, and with what" for the stage
 *  currently in flight — read off the diagnostics each job records when it starts
 *  (the Whisper model and device, the selector's provider and model, the render preset). */
function stageDetail(key: CheckpointKey, stage: PipelineStageStatus): string {
  const diagnostics = stage.diagnostics;
  const parts: string[] = [];
  const add = (value: string) => { if (value) parts.push(value); };

  if (stage.detail) add(stage.detail);

  switch (key) {
    case "transcription":
      add(text(diagnostics, "whisper_model") ? `whisper ${text(diagnostics, "whisper_model")}` : "");
      add(text(diagnostics, "whisper_device"));
      add(text(diagnostics, "whisper_compute_type"));
      if (diagnostics?.speaker_diarization_enabled === true) add("diarization");
      if (text(diagnostics, "operation") === "prepare_audio") add("extracting audio");
      break;
    case "selection":
      add(text(diagnostics, "provider"));
      add(text(diagnostics, "model"));
      add(text(diagnostics, "requested_moments") ? `${text(diagnostics, "requested_moments")} moments requested` : "");
      break;
    case "render":
      add(text(diagnostics, "render_preset"));
      break;
    default:
      break;
  }
  return parts.join(" · ");
}
